import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Button, Input, Select, Icon, Popover, InputNumber, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ModalBody,
  ModalContainer,
  InputLabel,
  Container,
  InputContainer,
  AddNewCategory,
  PopupContainer,
  PopupActionContainer,
  PopupLabel,
  SaveButton,
  CancelButton,
  DropdownContainer,
  WarningLabel,
  CategoryInputLabel,
  InputFlexColumn,
} from './styles';
import Loader from '../../components/Loader';
import NoTeam from '../../components/NoTeam';
import { paperSizes } from '../../utils/complexPaperSizes';
import { getAllCategories, createCategory } from '../../utils/services/categoriesAPI';
import { AppStoreContext } from '../../context/appStoreContext';
import { getGroupPermission } from '../../utils/services/teamsAPI';
import { getAllVariables, saveVariable } from '../../utils/services/variablesAPI';
import { createComplexDocument, patchComplexDocument, getMasterDocument } from '../../utils/services/complexDocumentsAPI';
import { DEFAULT_FONT_FOR_LOCALISED_LANGUAGES, handleCommunNameCheck, SUPPORT_EMAIL_ID } from '../../utils/helper';
import { deleteIndexedDBData } from '../../utils/indexedDBUtil';
import { CREATE_NEW_TEMPLATE_MODAL, CREATE_DOCUMENT_MODAL } from './translationValues';
import { measurementConversion } from '../../utils/javascript';
import { getPgNoDetailsAtIndex } from '../../containers/ComplexDocumentDesign/CreativeBlockDesign/PageNumber/pageNumberHelper';
import {
  useDocumentContext,
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
} from '../../context/complexDocumentContext';
import { importVariables } from '../../utils/importHelper';
import UpgradePlanModal from '../Modals/UpgradePlanModal';
import { importBusinessLogics, modifyMasterTemplateFlows } from '../../utils/masterTemplateHelper';
import { AppTourContext } from '../../context/appTourContext';
import { TEMPLATE_NAME_CHAR_LIMIT } from '../../utils/constants/appConstants';
import _ from 'underscore';
import { AvailableLanguagesDropdown, useSelectedTemplateLanguage } from './AvailableLanguagesDropdown';
import { isLanguageTranslationDisabled } from '../../utils/detectLanguageHelper';
import { useSessionContext } from '../../context/sessionContext';

const { Option } = Select;
const NAMESPACES = ['CREATE_NEW_TEMPLATE_MODAL', "CREATE_DOCUMENT_MODAL", "PAGE_NUMBER_TRANS"];
const CREATE_DOCUMENT = 'CREATE_DOCUMENT_MODAL';

const CreateDocTemplate = props => {
  const { onSucess, type, template, setDefaultTemplates, masterTemplate = {}, selectDesign, isEmailToDoc = false,
    categoryId = null, resourceGroupId = null, communicationId = null, isOpenDesign = false, autoCreateTemplate = false } = props;
  const { t, i18n } = useTranslation(NAMESPACES);
  const [templateName, setTemplateName] = useState(template ? template.name : '');
  const [validTempName, setValidTempName] = useState(template?.name ? true : false);
  const [validCategName, setValidCategName] = useState(false);
  const [savingLoader, setSavingLoader] = useState(false);
  const [allCategories, setAllCategories] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(isEmailToDoc || isOpenDesign ? categoryId : null);
  const [newCategoryName, setNewCategoryName] = useState(null);
  const [savePopupVisible, setSavePopupVisible] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [paperSize, setPaperSize] = useState('a4');
  const {
    userDetails,
    checkUserAccess,
    isAIGenerateText,
    setAIGenerateText,
    setGenerateContentForText,
    generateContentForText
  } = useContext(AppStoreContext);
  const organisationSelector = useCallback(store => store.organisationDetails, []);
  const [organisationDetails] = useSessionContext(organisationSelector);
  const { languages, templateLanguage, setTemplateLanguage } = useSelectedTemplateLanguage();
  const [warningText, setWarningText] = useState('');
  const [allTeams, setAllTeams] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(isEmailToDoc? resourceGroupId : null);
  const [pageLoading, setPageLoading] = useState(true);
  const [noTeam, setnoTeam] = useState(false);
  const [isComplex, setComplex] = useState(true);
  const [pageOrientation, setPageOrientation] = useState('portrait');
  const [pageSize, setPageSize] = useState({
    height: null,
    width: null,
  });
  const [sizeUnit, setSizeUnit] = useState('mm');
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);

  const contextSelector = useCallback(store => store, []);
  const [documentContext, store] = useDocumentContext(contextSelector);
  
  // Use isTourActive to check if the app tour is running, and handle the validation changes accordingly
  const { isTourActive, setRunAppTour, setStepIndex, handleTourValidation, setData } = useContext(AppTourContext);
  
  useEffect(() => {
    if (isTourActive && Object.keys(masterTemplate).length > 0) {
      setStepIndex(prev => prev + 1);
      handleTourValidation(false);
      setData(prevData => ({
        ...prevData,
        masterTemplateSelected: true
      }));
    } else {
      handleTourValidation(true);
    }
  }, [masterTemplate, isTourActive]);

  useEffect(() => {
    if (paperSize === 'custom')
      return;
    const sizes = paperSizes.filter(size => size.lowercaseName === paperSize)[0][pageOrientation];
    setPageSize({
      height: sizeUnit === 'mm' ? sizes.heightInMM : sizes.heightInPX,
      width: sizeUnit === 'mm' ? sizes.widthInMM : sizes.widthInPX,
    })
  }, [paperSize]);

  const handleUnitSizeChange = useCallback((value) => {
    setSizeUnit(value);
    let height = measurementConversion(pageSize.height, value);
    let width = measurementConversion(pageSize.width, value);
    setPageSize({ height, width });
  }, [pageSize]);

  const handlePageSizeChange = useCallback((value, key) => {
    setPageSize((prev) => ({ ...prev, [key]: value }))
  }, []);

  const userType = userDetails.userType;
  const currentLanguage = i18n.language;
  const documentLimit = userDetails?.planMapping?.templateLimit;

  useEffect(() => {
    (async () => {
      const response = await getAllCategories(false, 'template');
      if (response && response.data) {
        let loggedUserCategory = response.data.filter(
          categoryObj => userType === categoryObj.categoryusertype,
        );
        setAllCategories(loggedUserCategory);
      }
      const teamData = await getGroupPermission(true);
      if (teamData?.data) {
        setPageLoading(false);
        if (teamData.data.length === 1) {
          setSelectedGroup(teamData.data[0].rgId);
        }
        if (teamData.length === 0) {
          setnoTeam(true);
        }
        setAllTeams(teamData.data);
      } else {
        setnoTeam(true);
        setPageLoading(false);
      }
      if(isTourActive) {
        setStepIndex(prevIndex => prevIndex + 1);
        setData(prevData => ({
          ...prevData,
          teamLength: teamData.data.length
        }));
        setRunAppTour(true);
      }
    })();
    // eslint-disable-next-line
  }, [userType]);

  useEffect(() => {
    setGenerateContentForText('');
    setAIGenerateText(false);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if(autoCreateTemplate) {
      saveNewTemplate();
    }
  }, [autoCreateTemplate]);

  const saveNewTemplate = async () => {
    setSavingLoader(true);
    let payload = {
      name: templateName,
      thumbnail: null,
      shareType: 'department',
      categoryId: selectedCategoryId,
      resourceGroupId: selectedGroup,
      communicationId: communicationId,
      displayName: templateName,
      variableContract: [],
      style: {}
    };
    if (isLanguageTranslationDisabled(organisationDetails) && templateLanguage) {
      payload.language = templateLanguage;
    } else if (masterTemplate || template) { // Valid only for Saas use cases
      payload.language = masterTemplate?.language || template?.language;
    }
      payload.documentName = templateName;
      payload.style = {
        fontFamily: DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage),
        fontSize: 16
      }
      delete payload.name;
      delete payload.shareType;
      delete payload.thumbnail;
      const pageNumber = getPgNoDetailsAtIndex(0, t);
      payload.flows = [
        {
          orderNumber: 1,
          flowName: '',
          style: {
            padding: {
              left: 0,
              right: 0,
              top: 0,
              bottom: 0
            },
            width: sizeUnit === 'mm' ? measurementConversion(pageSize.width, 'px') : pageSize.width,
            height: sizeUnit === 'mm' ? measurementConversion(pageSize.height, 'px') : pageSize.height,
            backgroundColor: '#ffffff',
            pageNumber: pageNumber
          },
          paperSize,
          paperOrientation: pageOrientation,
          header: null,
          footer: null,
          blocks: []
        }
      ];


      const response = await createComplexDocument(payload, !isOpenDesign);
      if (response?.statusCode === 1933 && response?.errors?.some(error => error?.code === 2422)) {
        setSavingLoader(false);
        setIsLimitExceeded(true);
        return;
      }
      // const response = { data: { id: 1 } };
      // update masterTemplate
      let globalVariables = [];
      let variables = undefined;
      let businessLogics = undefined;
      let flows = undefined;
      let style = undefined;
      if (response?.data !== null && (masterTemplate.id || isOpenDesign)) {
        if (masterTemplate.id) {
          const payload = { draftDocumentId: masterTemplate.id }
          const masterTemplateResponse = await getMasterDocument(payload);
          globalVariables = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES];
          variables = masterTemplateResponse.data[0].variables;
          businessLogics = masterTemplateResponse.data[0].documentDTO.businessLogics;
          flows = masterTemplateResponse.data[0].documentDTO.flows;
          style = masterTemplate.style;
        } else if (isOpenDesign && template) {
          variables = [...store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES]];
          console.log("masterVariables :: ", store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES]);
          const response = await getAllVariables();
          if (response && response.errors?.length === 0) {
            globalVariables = response.data.map(variable => ({
                id: variable.id,
                variableCategoryId: variable.variableCategoryId,
                variableCategoryName: variable.variableCategoryName,
                name: variable.name,
                placeHolderText: variable.placeHolderText,
                exampleValue: variable.exampleValue,
                type: variable.type,
                length: variable.length,
                count: variable.count,
                mandatory: variable.mandatory,
                checkLength: variable.checkLength,
              }))
          }
          businessLogics = template.businessLogics;
          flows = template.flows;
          style = template.style;
        }

        const variableIdMap = await importVariables(variables, globalVariables, store);
        console.log({ variables, globalVariables, variableIdMap });
        const uploadedUrlsMap = new Map();
        const blBlocks = [];

        await modifyMasterTemplateFlows(flows, blBlocks, variableIdMap, uploadedUrlsMap, isOpenDesign);

        payload.documentId = response?.data.documentId;

        const updateDocumentFlow = [...flows, {
          flowId: response?.data.flowId,
          isDeleted: true,
          isStandalone: false
        }];

        const updateDocument = {
          documentName: templateName,
          documentId: response?.data.documentId,
          flows: updateDocumentFlow,
          style
        }

        const handleBusinessLogic = (syncData) => {
          return importBusinessLogics(businessLogics, variableIdMap, blBlocks, syncData);
        }
        await patchComplexDocument(updateDocument, undefined, handleBusinessLogic, false, false, false);
        if (isOpenDesign) {
          await deleteIndexedDBData('masterDocument');
          await deleteIndexedDBData('openImages');
          await deleteIndexedDBData('openDocument');
        }
      };

      if (response?.errors?.length > 0 && response.errors[0] && response.errors.every(err => err.code !== 3003)) {
        if(isTourActive) {
          setStepIndex(2);
          setData(prevData => ({
            ...prevData,
            tempNameExists: true
          }));
        }
        message.error(
          t(
            CREATE_NEW_TEMPLATE_MODAL.UNABLE_To_CREATE_TEMPLATE.key,
            CREATE_NEW_TEMPLATE_MODAL.UNABLE_To_CREATE_TEMPLATE.value,
            { type: 'Document' },
          )
        );
}

      if(response?.data !== null){
        if(isTourActive) {
          setRunAppTour(false);
        }
        onSucess(response?.data, 'isComplex');        
        return;
      };
      setSavingLoader(false);        
  };

  const setCategoryName = categoryName => {
    setNewCategoryName(categoryName.trimStart());
    if (categoryName.split(" ").join("").length) {
      setValidCategName(true);
    }
    else {
      setValidCategName(false)
    }
  };

  const handlePopupVisibleChange = visible => {
    setSavePopupVisible(visible);
  };

  const saveNewCategory = () => {
    setSavingCategory(true);
    let payload = {
      name: newCategoryName,
      type: 'template',
    };
    createNewCategory(payload);
  };

  const createNewCategory = async payload => {
    let categoryResponse = await createCategory(payload);
    if (categoryResponse && categoryResponse.errors && categoryResponse.errors.length === 0) {
      const savedCategory = {
        createdBy: null,
        createdOn: null,
        elements: null,
        id: categoryResponse.id,
        name: payload.name,
        parentId: 0,
        status: 'active',
        type: 'template',
        updatedBy: null,
        updatedOn: null,
      };
      setAllCategories(allCategories => [...allCategories, savedCategory]);
      setSavePopupVisible(false);
      setSelectedCategoryId(categoryResponse.id);
      if(isTourActive) {
        allTeams.length > 1 ? setStepIndex(prev => prev + 1) : setStepIndex(prev => prev + 2);
        handleTourValidation(false);
      }
      setNewCategoryName(null);
      setSavingCategory(false);
    } else {
      setSavingCategory(false);
    }
  };

  const handleSizeChange = size => {
    setPaperSize(size);
  };

  const debouncedNameCheck = useCallback(
    _.debounce(async (name) => {
      const nameCheck = await handleCommunNameCheck(name);
      if (nameCheck.length > 0) {
        setValidTempName(false)
        name !== "" && setWarningText(t(CREATE_NEW_TEMPLATE_MODAL.COMMUNICATION_NAME_VALIDATION.key, CREATE_NEW_TEMPLATE_MODAL.COMMUNICATION_NAME_VALIDATION.value));
      } else {
        setValidTempName(true)
        setWarningText('');
      }
    }, 500),
    []
  );

  const changeTempName = name => {
    setTemplateName(name.trimStart())
    if (name.split(" ").join("").length) {
      if(name.trimStart().length > TEMPLATE_NAME_CHAR_LIMIT) {
        setWarningText(t(CREATE_NEW_TEMPLATE_MODAL.CHAR_LIMIT_EXCEEDED_ERROR.key, CREATE_NEW_TEMPLATE_MODAL.CHAR_LIMIT_EXCEEDED_ERROR.value, { charLimitLength: TEMPLATE_NAME_CHAR_LIMIT }));
        setValidTempName(false);
        return;
      }
      setWarningText('');
      setValidTempName(true);
      isTourActive && handleTourValidation(false);
    }
    else {
      setWarningText(t(CREATE_NEW_TEMPLATE_MODAL.ENTER_VALID_TEMPLATE_NAME_WARNING_MESSAGE.key, CREATE_NEW_TEMPLATE_MODAL.ENTER_VALID_TEMPLATE_NAME_WARNING_MESSAGE.value));
      setValidTempName(false)
      isTourActive && handleTourValidation(true);
    }
    debouncedNameCheck(name);
  };

  return (
    <Container onKeyDown={e => e.stopPropagation()}>
      <ModalBody>
        <ModalContainer isAIText={isAIGenerateText}>
          {pageLoading ? (
            <Loader />
          ) : noTeam ? (
            <NoTeam />
          ) : (
            <>
              <InputContainer
                 className={'template-name-input'}>
                <InputLabel>{t(CREATE_NEW_TEMPLATE_MODAL.TEMPLATE_NAME_LABEL.key, CREATE_NEW_TEMPLATE_MODAL.TEMPLATE_NAME_LABEL.value)}</InputLabel>
                <Input
                  placeholder={t(CREATE_NEW_TEMPLATE_MODAL.TEMPLATE_NAME_PLACEHOLDER.key, CREATE_NEW_TEMPLATE_MODAL.TEMPLATE_NAME_PLACEHOLDER.value)}
                  autoComplete="off"
                  name="templateName"
                  onChange={e => {
                    changeTempName(e.target.value);
                    // setWarningText('');
                  }}
                  size="large"
                  value={templateName}
                  disabled={communicationId !== null}
                  autoFocus
                />
                {warningText && <WarningLabel>{warningText}</WarningLabel>}
              </InputContainer>
              <InputContainer className={'template-category-input'}>
                <InputFlexColumn>
                  <AddNewCategory>
                    <CategoryInputLabel>{t(CREATE_NEW_TEMPLATE_MODAL.SELECT_CATEGORY_LABEL.key, CREATE_NEW_TEMPLATE_MODAL.SELECT_CATEGORY_LABEL.value)}</CategoryInputLabel>
                    <Popover
                      content={
                        <PopupContainer>
                          <PopupLabel>{t(CREATE_NEW_TEMPLATE_MODAL.CATEGORY_NAME_LABEL.key, CREATE_NEW_TEMPLATE_MODAL.CATEGORY_NAME_LABEL.value)}</PopupLabel>
                          <Input
                            placeholder={t(CREATE_NEW_TEMPLATE_MODAL.CATEGORY_NAME_PLACEHOLDER.key, CREATE_NEW_TEMPLATE_MODAL.CATEGORY_NAME_PLACEHOLDER.value)}
                            autoComplete="off"
                            name="categoryName"
                            value={newCategoryName}
                            onChange={e => setCategoryName(e.target.value)}
                          />
                          <PopupActionContainer>
                            <CancelButton
                              size="default"
                              onClick={() => handlePopupVisibleChange(false)}
                            >
                              {t(CREATE_NEW_TEMPLATE_MODAL.CANCEL_BUTTON.key, CREATE_NEW_TEMPLATE_MODAL.CANCEL_BUTTON.value)}
                            </CancelButton>
                            <SaveButton
                              type="primary"
                              size="default"
                              onClick={() => saveNewCategory()}
                              disabled={!newCategoryName || !validCategName}
                              loading={savingCategory}
                            >
                              {savingCategory ? t(CREATE_NEW_TEMPLATE_MODAL.SAVING_CATEGORY_LOADING_TEXT.key, CREATE_NEW_TEMPLATE_MODAL.SAVING_CATEGORY_LOADING_TEXT.value) : t(CREATE_NEW_TEMPLATE_MODAL.SAVE_BUTTON.key, CREATE_NEW_TEMPLATE_MODAL.SAVE_BUTTON.value)}
                            </SaveButton>
                          </PopupActionContainer>
                        </PopupContainer>
                      }
                      placement="leftBottom"
                      trigger="click"
                      visible={savePopupVisible}
                      onVisibleChange={handlePopupVisibleChange}
                    >
                      <Icon type="plus" /> {t(CREATE_NEW_TEMPLATE_MODAL.ADD_NEW_ACTION.key, CREATE_NEW_TEMPLATE_MODAL.ADD_NEW_ACTION.value)}
                    </Popover>
                  </AddNewCategory>
                    <Select
                    loading={!allCategories}
                    showSearch
                    placeholder={t(CREATE_NEW_TEMPLATE_MODAL.SELECT_CATEGORY_PLACEHOLDER.key, CREATE_NEW_TEMPLATE_MODAL.SELECT_CATEGORY_PLACEHOLDER.value)}
                    optionFilterProp="children"
                    value={selectedCategoryId ? selectedCategoryId : undefined}
                    onChange={value => {
                      setSelectedCategoryId(value);
                      if(isTourActive) {
                        allTeams.length > 1 ? setStepIndex(prev => prev + 1) : setStepIndex(prev => prev + 2);
                        handleTourValidation(false);
                      }
                    }}
                    filterOption={(input, option) =>
                      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {allCategories &&
                      allCategories.map(data => {
                        return (
                          <Option key={String(data.id)} value={data.id}>
                            {data.name}
                          </Option>
                        );
                      })}
                  </Select>
                </InputFlexColumn>
              </InputContainer>

              {allTeams.length > 1 && (
                <InputContainer className={'template-team-input'}>
                  <InputLabel>{t(CREATE_NEW_TEMPLATE_MODAL.SELECT_TEAM_LABEL.key, CREATE_NEW_TEMPLATE_MODAL.SELECT_TEAM_LABEL.value)}</InputLabel>
                  <Select
                    loading={!allTeams}
                    showSearch
                    placeholder={t(CREATE_NEW_TEMPLATE_MODAL.SELECT_TEAM_PLACEHOLDER.key, CREATE_NEW_TEMPLATE_MODAL.SELECT_TEAM_PLACEHOLDER.value)}
                    optionFilterProp="children"
                    value={selectedGroup || undefined}
                    onChange={value => { 
                      setSelectedGroup(value); 
                      if(isTourActive) {
                        setStepIndex(prev => prev + 1);
                        handleTourValidation(false); 
                      }
                    }}
                    filterOption={(input, option) =>
                      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {allTeams &&
                      allTeams.map(data => {
                        return (
                          <Option key={String(data.id)} value={data.rgId}>
                            {data.groupName}
                          </Option>
                        );
                      })}
                  </Select>
                </InputContainer>
              )}
              {!isOpenDesign && <InputContainer className="flex-container">
                <DropdownContainer className="full-width">
                  <InputLabel>
                    {isComplex
                      ? t(`${CREATE_DOCUMENT}:${CREATE_DOCUMENT_MODAL.COMPLEX_PAGE_SIZE_DROPDOWN_OPTION_LABEL.key}`,
                        CREATE_DOCUMENT_MODAL.COMPLEX_PAGE_SIZE_DROPDOWN_OPTION_LABEL.value)
                      : t(`${CREATE_DOCUMENT}:${CREATE_DOCUMENT_MODAL.SIZE_DROPDOWN_OPTION_LABEL.key}`,
                        `${CREATE_DOCUMENT}:${CREATE_DOCUMENT_MODAL.SIZE_DROPDOWN_OPTION_LABEL.value}`)}
                  </InputLabel>
                  <Select
                    disabled={type === 'cloneTemplate'}
                    value={paperSize}
                    onChange={handleSizeChange}
                  >
                    <Option value="a4">A4 - 210 x 297 mm</Option>
                    <Option value={isComplex ? "us_letter" : "us-letter"}>US Letter - 216 x 279 mm</Option>
                    <Option value={isComplex ? "us_legal" : "us-legal"}>US Legal - 216 x 356 mm</Option>
                    <Option value="custom">{t(`${CREATE_DOCUMENT}:${CREATE_DOCUMENT_MODAL.CUSTOM_PAPER_SIZE_LABEL.key}`,
                      CREATE_DOCUMENT_MODAL.CUSTOM_PAPER_SIZE_LABEL.value)}</Option>
                  </Select>
                </DropdownContainer>
              </InputContainer>}
              {paperSize === 'custom' && (<div style={{ display: 'flex', gap: '24px' }}>
                <InputContainer>
                  <InputLabel>{t(`${CREATE_DOCUMENT}:${CREATE_DOCUMENT_MODAL.CUSTOM_HEIGHT_LABEL.key}`,
                    CREATE_DOCUMENT_MODAL.CUSTOM_HEIGHT_LABEL.value)}</InputLabel>
                  <InputNumber
                    type='number'
                    min={1}
                    autoComplete="off"
                    size='large'
                    value={pageSize.height}
                    onChange={e => handlePageSizeChange(e, 'height')}
                  />
                </InputContainer>
                <InputContainer>
                  <InputLabel>{t(`${CREATE_DOCUMENT}:${CREATE_DOCUMENT_MODAL.CUSTOM_WIDTH_LABEL.key}`,
                    CREATE_DOCUMENT_MODAL.CUSTOM_WIDTH_LABEL.value)}</InputLabel>
                  <InputNumber
                    type="number"
                    min={1}
                    autoComplete="off"
                    size='large'
                    value={pageSize.width}
                    onChange={e => handlePageSizeChange(e, 'width')}
                  />
                </InputContainer>
                <InputContainer>
                  <InputLabel>{t(`${CREATE_DOCUMENT}:${CREATE_DOCUMENT_MODAL.SIZE_IN_LABEL.key}`,
                    CREATE_DOCUMENT_MODAL.SIZE_IN_LABEL.value)}</InputLabel>
                  <Select
                    value={sizeUnit}
                    onChange={(e) => handleUnitSizeChange(e)}
                  >
                    <Option value="px">px</Option>
                    <Option value="mm">mm</Option>
                  </Select>
                </InputContainer>
              </div>)}
              <AvailableLanguagesDropdown
                languages={languages}
                templateLanguage={templateLanguage}
                setTemplateLanguage={setTemplateLanguage}
              />
                  {selectDesign && <div className='master-template'>
                    <div onClick={() => {
                      setDefaultTemplates();
                      if(isTourActive && Object.keys(masterTemplate).length === 0)
                      setStepIndex(prevIndex => prevIndex + 1);
                    }} className='btn'>
                      <div className='btn-text'>{Object.keys(masterTemplate).length > 0 ? masterTemplate.name : t(CREATE_NEW_TEMPLATE_MODAL.CLICK_TO_SELECT_BUTTON.key, CREATE_NEW_TEMPLATE_MODAL.CLICK_TO_SELECT_BUTTON.value)}</div>
                      {Object.keys(masterTemplate).length > 0 &&
                      <div className="preview-button1">
                        <div class="wrapper">
                          <div class="circle">
                            <div class="checkMark"></div>
                          </div>
                        </div>
                      </div>
                    }
                    </div>

                    <div className='desc'> {t(CREATE_NEW_TEMPLATE_MODAL.MASTER_TEMPLATE_DESC.key, CREATE_NEW_TEMPLATE_MODAL.MASTER_TEMPLATE_DESC.value)}</div>
                  </div>}
              <InputContainer>
                <div className="form_btn">
                  <Button
                    key="submit"
                    type="primary"
                    disabled={!templateName || !validTempName || !selectedCategoryId || (checkUserAccess('client') && !selectedGroup) || isAIGenerateText && generateContentForText.length === 0 || (paperSize === 'custom' && (pageSize.height < 1 || pageSize.width < 1))}
                    // TODO : Temporary change to be reverted once category creation is working fine
                    // disabled={isComplex ? !templateName || !validTempName || (checkUserAccess('client') && !selectedGroup) : !templateName || !validTempName || !selectedCategoryId || (checkUserAccess('client') && !selectedGroup)}
                    loading={savingLoader}
                    onClick={saveNewTemplate}
                  >
                    {savingLoader ? t(CREATE_NEW_TEMPLATE_MODAL.START_DESIGN_BUTTON.key, CREATE_NEW_TEMPLATE_MODAL.START_DESIGN_BUTTON.value) : t(CREATE_NEW_TEMPLATE_MODAL.START_DESIGN_BUTTON.key, CREATE_NEW_TEMPLATE_MODAL.START_DESIGN_BUTTON.value)}
                  </Button>
                </div>
              </InputContainer>
            </>
          )}
        </ModalContainer>
      </ModalBody>
      <UpgradePlanModal 
        visible={isLimitExceeded}
        modalText={
          userDetails?.planMapping?.planName === 'Premium' ?
            t(CREATE_NEW_TEMPLATE_MODAL.PREMIUM_DOCUMENT_CREATION_LIMIT_EXCEEDED.key, CREATE_NEW_TEMPLATE_MODAL.PREMIUM_DOCUMENT_CREATION_LIMIT_EXCEEDED.value, { emailId: SUPPORT_EMAIL_ID })
          : userDetails?.planMapping?.planName === 'Free' ? t(CREATE_NEW_TEMPLATE_MODAL.FREE_PLAN_UPGRADE_MESSAGE.key, CREATE_NEW_TEMPLATE_MODAL.FREE_PLAN_UPGRADE_MESSAGE.value, { documentLimit })
          : t(CREATE_NEW_TEMPLATE_MODAL.STANDARD_PLAN_UPGRADE_MESSAGE.key, CREATE_NEW_TEMPLATE_MODAL.STANDARD_PLAN_UPGRADE_MESSAGE.value, { documentLimit })
        }
        onCancel={() => setIsLimitExceeded(false)}
      />
    </Container>
  );
};
export default CreateDocTemplate;
